import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Info } from "@phosphor-icons/react";

// Customer portal only — page-specific status tile.
//
// Phase CUSTOMER-PORTAL-PAGE-SPECIFIC-DATA-STATUS-R1 (2026-02-09):
// The tile is no longer mounted globally by CustomerLayout. It is
// mounted ONLY by the Price Lists and Transactions pages, and the
// caller passes `variant` to pick which of the two status lines
// renders alongside the always-shown "Final Review" line.
//
//   variant="prices"       -> Prices  +  Final Review
//   variant="transactions" -> Transactions  +  Final Review
//
// Copy change: "Next-day price updates ... 5:00 PM (ET)" was moved
// to 7:00 PM (ET) per operator spec 2026-02-09. Transaction timing
// (10:00 AM ET) is unchanged.
//
// Authoritative timestamp sources (unchanged, from GET /api/portal/banner):
//   * last_price_update       — `price_list_uploads.created_at` (most recent,
//                               scoped to the customer's allowed networks).
//                               Only written after a SUCCESSFUL upload.
//   * last_transaction_update — `transaction_uploads.created_at` (most recent
//                               where `customers_seen` includes the caller's
//                               customer_id, with fallback to the latest
//                               transactions row's `created_at` for that
//                               customer). Only written after a SUCCESSFUL
//                               upload.
//
// The customer cannot modify either timestamp — the endpoint is read-only.

const ET_FMT = new Intl.DateTimeFormat("en-GB", {
  timeZone: "America/Toronto",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function formatEt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  // "25 Aug 2026, 5:12 p.m." → "25 Aug 2026, 5:12 PM ET"
  // Uses IANA `America/Toronto` so daylight saving (EST↔EDT) is handled
  // automatically. Display abbreviation is the generic "ET" per operator
  // spec 2026-02-12.
  const raw = ET_FMT.format(d);
  return `${raw.replace(/\s?(a\.?m\.?|p\.?m\.?)/i, (m) => " " + m.replace(/\./g, "").trim().toUpperCase())} ET`;
}

export default function PortalBanner({ variant }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/portal/banner")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  const lastPrice = formatEt(data?.last_price_update);
  const lastTx = formatEt(data?.last_transaction_update);

  const showPrices = variant === "prices";
  const showTransactions = variant === "transactions";

  return (
    <div className="portal-banner mb-6" data-testid="portal-banner">
      <div className="gf-card-body">
        <div className="flex items-start gap-3">
          <div style={{ color: "#1A3FBA", marginTop: 2 }}>
            <Info size={18} weight="duotone" />
          </div>
          <div className="flex-1">
            <div
              className="overline mb-2 portal-banner-title"
              data-testid="portal-banner-title"
            >
              SCHEDULE &amp; DATA STATUS
            </div>
            <ol
              className="space-y-2 text-sm list-decimal pl-5"
              style={{ color: "#374151" }}
            >
              {showPrices && (
                <li data-testid="portal-banner-msg-prices">
                  <strong>Prices:</strong> Next-day price updates are generally
                  completed by 7:00 PM (ET).
                  <div
                    className="text-xs font-mono mt-0.5"
                    style={{ color: "#1F2937" }}
                    data-testid="portal-banner-last-price"
                  >
                    Last price update: {lastPrice}
                  </div>
                </li>
              )}
              {showTransactions && (
                <li data-testid="portal-banner-msg-transactions">
                  <strong>Transactions:</strong> Previous-day transaction updates
                  are generally completed by 10:00 AM (ET).
                  <div
                    className="text-xs font-mono mt-0.5"
                    style={{ color: "#1F2937" }}
                    data-testid="portal-banner-last-tx"
                  >
                    Last transaction update: {lastTx}
                  </div>
                </li>
              )}
              <li data-testid="portal-banner-msg-final-review">
                <strong>Final Review:</strong> All prices and transaction data
                remain subject to final review and confirmation. Updates may be
                revised following review.
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
